/**
 * Privacy consent state management.
 *
 * Manages user privacy preferences with dual persistence:
 * - `useState` for reactive SSR-compatible state
 * - `useLocalStorage` for remembering choices across sessions
 *
 * When preferences change, they are synced to GA and HubSpot consent APIs.
 */

import { DEFAULT_PRIVACY_PREFS } from '~/constants'

export interface PrivacyPrefs {
  analytics: boolean
  marketing: boolean
  functionality: boolean
}

export function usePrivacy() {
  const privacyState = useState<PrivacyPrefs>('privacy', () => DEFAULT_PRIVACY_PREFS)

  const privacyLocal = useLocalStorage<{ prefs: PrivacyPrefs | null }>('ics-privacy', { prefs: null })

  const readPrefs = computed(() => ({ ...privacyState.value }))

  const readLocalPrefs = computed(() => privacyLocal.value.prefs ? { ...privacyLocal.value.prefs } : null)

  const privacyIsDefault = computed(() =>
    Object.entries(readPrefs.value).every(([key, value]) => value === DEFAULT_PRIVACY_PREFS[key as keyof PrivacyPrefs]))

  watch(readPrefs, () => {
    privacyLocal.value.prefs = readPrefs.value
  })

  const { updateConsentOptions: updateGAConsent } = useGoogleAnalytics()
  const { updateConsentOptions: updateHSConsent } = useHubspot()

  /**
   * Updates privacy preferences and syncs to third-party consent APIs.
   * Converts user-friendly prefs (analytics/marketing) to platform-specific consent.
   * @param prefs - User's privacy preferences
   */
  function updatePrefs(
    prefs: MaybeRefOrGetter<PrivacyPrefs>,
  ) {
    const newPrefs = toValue(prefs)
    privacyState.value = newPrefs

    updateGAConsent(newPrefs)
    updateHSConsent(newPrefs)
  }

  /** Grants all privacy consents and updates GA/HubSpot */
  function acceptAll() {
    privacyState.value = {
      analytics: true,
      marketing: true,
      functionality: true,
    }
    updateGAConsent(privacyState.value)
    updateHSConsent(privacyState.value)
  }

  /** Denies all privacy consents and updates GA/HubSpot */
  function rejectAll() {
    privacyState.value = DEFAULT_PRIVACY_PREFS
    updateGAConsent(privacyState.value)
    updateHSConsent(privacyState.value)
  }

  /** Restores privacy state from localStorage and applies to GA/HubSpot (used on client hydration) */
  function restoreFromLocal() {
    const localState = privacyLocal.value.prefs
    if (localState)
      updatePrefs(localState)
  }

  return {
    privacyState,
    privacyLocal,
    readPrefs,
    readLocalPrefs,
    privacyIsDefault,
    updatePrefs,
    acceptAll,
    rejectAll,
    restoreFromLocal,
  }
}
